import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import { NotificationTypes } from '../../../../../../components/Notification/notificationEnums';
import HttpStatus from '../../../../../../enums/httpStatus';
import { apiKeysReq } from '../../../../../../services/requests';
import { ApiKey } from '../../../../../../types';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  callback: () => void;
  apiKeySelected?: ApiKey | null;
};

const formatDateTimeLocal = (dateValue: string | null) => {
  if (!dateValue) {
    return '';
  }

  return dateValue.replace(' ', 'T').replace('.000000Z', '').replace('Z', '').slice(0, 16);
};

const formatDateToBackend = (dateValue: string) => {
  if (!dateValue) {
    return null;
  }

  return `${dateValue.replace('T', ' ')}:00`;
};

const CreateOrEditApiKeyModal = ({
  showModal,
  closeModal,
  callback,
  apiKeySelected,
}: Props) => {
  const { t } = useTranslation();

  const isEditMode = Boolean(apiKeySelected?.code);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [hasExpiration, setHasExpiration] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const now = new Date();
  const minDateTime = now.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"

  useEffect(() => {
    if (!showModal) {
      return;
    }

    if (apiKeySelected) {
      setName(apiKeySelected.name);
      setHasExpiration(apiKeySelected.has_expiration);
      setExpiresAt(formatDateTimeLocal(apiKeySelected.expires_at));
    } else {
      setName('');
      setHasExpiration(false);
      setExpiresAt('');
    }

    setPassword('');
  }, [apiKeySelected, showModal]);

  const saveBtnAction = async () => {
    setIsLoading(true);

    try {
      const payload = {
        name: name.trim(),
        password,
        has_expiration: hasExpiration,
        expires_at: hasExpiration ? formatDateToBackend(expiresAt) : null,
      };

      apiKeysReq.query = '';

      const response = isEditMode && apiKeySelected
        ? await apiKeysReq.update(payload, apiKeySelected.code)
        : await apiKeysReq.store(payload);

      if (
        response.status === HttpStatus.OK
        || response.status === HttpStatus.CREATED
      ) {
        frontendNotification({
          message: isEditMode ? t('updatedApiKey') : t('createdApiKey'),
          type: NotificationTypes.SUCCESS,
        });

        callback();
        closeModal();
      } else {
        throw response;
      }
    } catch (err) {
      if (!apiKeysReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  return (
    <Modal
      title={t(isEditMode ? 'editApiKey' : 'createApiKey')}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={saveBtnAction}
      actionNameBtn={t(isEditMode ? 'save' : 'create')}
      disableActionBtn={!name.trim() || !password || (hasExpiration && !expiresAt)}
      isLoading={isLoading}
      bodyStyle={{ overflow: 'scroll' }}
    >
      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('apiKeyName') as string}</p>
          <input
            className="input"
            type="text"
            value={name}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.currentTarget.value)}
          />
        </div>

        <div className="inputWithLabel">
          <p className="label">{t('confirmPassword') as string}</p>
          <input
            className="input"
            type="password"
            value={password}
            onChange={
              (event: ChangeEvent<HTMLInputElement>) => setPassword(event.currentTarget.value)
            }
          />
        </div>
      </div>

      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('apiKeyHasExpiration') as string}</p>
          <label
            htmlFor="api-key-has-expiration"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <input
              id="api-key-has-expiration"
              type="checkbox"
              checked={hasExpiration}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setHasExpiration(event.currentTarget.checked);

                if (!event.currentTarget.checked) {
                  setExpiresAt('');
                }
              }}
            />
            <span>{t('yes')}</span>
          </label>
        </div>

        {hasExpiration && (
          <div className="inputWithLabel">
            <p className="label">{t('expirationDate') as string}</p>
            <input
              className="input"
              type="datetime-local"
              value={expiresAt}
              onChange={
                (event: ChangeEvent<HTMLInputElement>) => setExpiresAt(event.currentTarget.value)
              }
              min={minDateTime}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

CreateOrEditApiKeyModal.defaultProps = {
  apiKeySelected: null,
};

export default CreateOrEditApiKeyModal;